import { m } from "framer-motion";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useRouter } from "src/hooks/useRouter";
import { useAuthData } from "src/auth/firebase/AuthProvider";
import { useSnackbar } from "notistack";
import CustomPopover from "./CustomPopover";
import usePopover from "src/hooks/usePopover";
import axios from "axios";

const OPTIONS = [
  {
    label: "マイページ",
    // linkTo: "/mypage",
    linkTo: "/mypage/plan-management",
  },
];

export default function AccountPopover() {
  const router = useRouter();
  const theme = useTheme();
  const { logout, user } = useAuthData();
  const { enqueueSnackbar } = useSnackbar();
  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace("/auth/login");
      enqueueSnackbar("ログアウトしました", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("ログアウトに失敗しました", { variant: "error" });
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: "rgba(145, 158, 171, 0.08)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            transform: "scale(1.05)",
          },
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, #5BE49B 0%, #00A76F 100%)`,
            boxShadow: "0 4px 20px rgba(91, 228, 155, 0.3)",
          }),
        }}
      >
        <Avatar
          src={user?.photoURL}
          alt={user?.displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.paper}`,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        />
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{
          width: 280,
          p: 0,
          "& .MuiPaper-root": {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(145, 158, 171, 0.12)",
          },
        }}
      >
        <m.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* ユーザー情報セクション */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.displayName || "ユーザー"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>

          {/* メニューアイテム */}
          <Box sx={{ p: 1 }}>
            {/* アカウント作成（管理者のみ） */}
            {user?.role === "admin" && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  router.replace("/dashboard/account-create");
                }}
                sx={{
                  borderRadius: 2,
                  mx: 0.5,
                  mb: 0.5,
                  py: 1.5,
                  px: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.warning.main,
                      0.08
                    )} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
                    transform: "translateX(4px)",
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  アカウント作成
                </Typography>
              </MenuItem>
            )}

            <Divider sx={{ my: 1, borderStyle: "dashed", opacity: 0.6 }} />

            {/* ログアウト */}
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                mx: 0.5,
                py: 1.5,
                px: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.error.main,
                    0.08
                  )} 0%, ${alpha(theme.palette.error.main, 0.04)} 100%)`,
                  transform: "translateX(4px)",
                },
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.error.main,
                }}
              >
                ログアウト
              </Typography>
            </MenuItem>
          </Box>
        </m.div>
      </CustomPopover>
    </>
  );
}
